import type { Invitation } from '@app-types';
import { Badge } from '@common';

interface PayorPaymentStatusProps {
    invitations: Invitation[];
    currentPayorId: number;
}

const PayorPaymentStatus = ({ invitations, currentPayorId }: PayorPaymentStatusProps) => {
    /**
     * Calculate display percentages using the largest remainder method.
     *
     * Goals:
     * 1. All displayed percentages must sum to exactly 100%
     * 2. Never round non-zero percentages down to 0%
     * 3. Never show 100% when there are multiple payors
     *
     * Examples:
     * - Input: [33.33%, 33.33%, 33.34%] → Output: [33%, 33%, 34%] (sum = 100%)
     * - Input: [0.1%, 49.9%, 50%] → Output: [1%, 49%, 50%] (no zeros, sum = 100%)
     * - Input: [99.9%, 0.1%] → Output: [99%, 1%] (no 100% with multiple, sum = 100%)
     * - Input: [99%, 0.5%, 0.5%] → Output: [98%, 1%, 1%] (constraints met, sum = 100%)
     * - Input: [25.1%, 25.1%, 25.1%, 24.7%] → Output: [25%, 25%, 25%, 25%] (sum = 100%)
     */
    const calculateDisplayPercentages = (invitations: Invitation[]): Map<number, number> => {
        const hasMultiple = invitations.length > 1;

        const items = invitations.map((inv, index) => {
            const percentage = inv.payPercentage * 100;
            const floor = Math.floor(percentage);
            return {
                index,
                idPayor: inv.idPayor,
                percentage,
                floor,
                remainder: percentage - floor,
                rounded: floor
            };
        });

        // Apply constraints immediately
        for (const item of items) {
            // Never show 0% for non-zero percentages
            if (item.percentage > 0 && item.rounded === 0) {
                item.rounded = 1;
            }
            // Never show 100% if there are multiple payors
            if (hasMultiple && item.rounded >= 100) {
                item.rounded = 99;
            }
        }

        // Calculate remaining points to distribute
        const currentSum = items.reduce((sum, item) => sum + item.rounded, 0);
        let remaining = 100 - currentSum;

        // Sort by remainder descending
        items.sort((a, b) => b.remainder - a.remainder);

        // Distribute remaining points (positive or negative)
        let i = 0;
        while (remaining !== 0 && i < items.length * 10) { // Safety limit
            const item = items[i % items.length];
            if (!item) break; // For TS compiler; modulo guarantees this exists

            if (remaining > 0 && (!hasMultiple || item.rounded < 99)) {
                item.rounded++;
                remaining--;
            } else if (remaining < 0 && item.rounded > 1) {
                item.rounded--;
                remaining++;
            }

            i++;
        }

        const map = new Map<number, number>();
        items.forEach(item => {
            map.set(item.idPayor, item.rounded);
        });
        return map;
    };

    const displayPercentages = calculateDisplayPercentages(invitations);

    // Sort invitations to put current user first
    const sortedInvitations = [...invitations].sort((a, b) => {
        if (a.idPayor === currentPayorId) return -1;
        if (b.idPayor === currentPayorId) return 1;
        return 0;
    });

    return (
        <div className="flex flex-col gap-2 w-full md:w-[420px]">
            {sortedInvitations.map((invitation) => {
                const isCurrentUser = invitation.idPayor === currentPayorId;
                const displayName = isCurrentUser ? `You` : invitation.fullName;
                const isPaid = invitation.balance === 0;
                const displayPercentage = displayPercentages.get(invitation.idPayor) || 0;

                return (
                    <div
                        key={invitation.idPayor}
                        className="bg-white border border-neutral-100 rounded-lg pl-2 pr-1 py-1 flex items-center justify-between">
                        <p className="text-sm leading-5 text-neutral-950 tracking-[0.14px] truncate min-w-0 flex-1 mr-2">
                            {displayName}
                        </p>
                        <div className="flex gap-2 items-center flex-shrink-0">
                            <Badge>{displayPercentage}%</Badge>
                            <Badge color={isPaid ? `green` : `red`}>
                                {isPaid ? `Paid` : `Unpaid`}
                            </Badge>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default PayorPaymentStatus;
